<template>
    <div :class="searchBarClass">
        <template v-if="!isLoading">
            <StreamlineIcon
                :icon="Search"
                class="SearchBar__search-icon"
                :stroke="grey"
                :size="16"
            />
            <!--
                Used for v-model
                @event input
            -->
            <!--
                Trigger a search when user clicks `enter`
                @event submit
                @property {object} event
            -->
            <!--
                Triggered every time the search text changes
                @event change
                @property {object} event
            -->
            <input
                ref="input"
                :value="value"
                :class="searchClass"
                type="text"
                :placeholder="placeholder"
                :disabled="disabled"
                @change="e => $emit('change', e.target.value)"
                @input="input"
                @keypress.enter.stop="submit"
                @focus="focusEvent"
                @blur="blurEvent"
            >

            <template v-if="value.length">
                <span
                    v-if="!isError"
                    class="SearchBar__clear-icon"
                    @click="clearInput"
                    @mouseenter="hover=true"
                    @mouseleave="hover=false"
                >
                    <RPLoadingIcon
                        v-if="isFetching && !hover"
                    />
                    <StreamlineIcon
                        v-else
                        :icon="Delete1"
                        :stroke="white"
                        :fill="grey"
                        :size="12"
                    />
                </span>
                <span
                    v-else
                    @click="retryInput"
                >
                    <StreamlineIcon
                        class="SearchBar__clear-icon"
                        :icon="DiagramRound"
                        :fill="error"
                        :stroke="error"
                        :size="12"
                    />
                </span>
            </template>
        </template>
    </div>
</template>
<script>
import stat from 'status/index';
import statusBuilder from 'status/statusBuilder';
import StreamlineIcon from "@reviewpush/streamline-icons-vue";
import {Delete1} from "@reviewpush/streamline-regular/lib/interface-essential/Delete";
import {Search} from "@reviewpush/streamline-regular/lib/interface-essential/Search";
import debounce from 'lodash/debounce';
import RPLoadingIcon from '@/components/Basic/Misc/RPLoadingIcon.vue';
import {white, grey, error} from 'style/_colors.scss';
import {DiagramRound} from "@reviewpush/streamline-bold/lib/arrows-diagrams/Diagrams";

export default {
    name: 'RPSearchBar',
    components: {
        StreamlineIcon,
        RPLoadingIcon,
    },
    props: {
        /**
         * @model
         */
        value: {
            type: String,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        /**
         * @values white, grey
         */
        backgroundColor: {
            type: String,
            default: 'white',
            validator: val => ['white', 'grey'].indexOf(val) !== -1,
        },
        status: {
            type: String,
            default: stat.STANDBY,
        }
    },
    data(){
        return {
            hover: false,
            placeholder: 'Search',

            Delete1, white, grey600,
            Search, grey,
            DiagramRound, error
        };
    },
    computed: {
        isLoading(){
            return this.status === stat.LOADING;
        },
        isFetching(){
            return this.status === stat.FETCHING;
        },
        isError(){
            return this.status === stat.ERROR;
        },
        searchBarClass(){
            return statusBuilder('SearchBar', this.status);
        },
        searchClass(){
            return statusBuilder(
                'SearchBar__search',
                this.status,
                this.disabled,
                {
                    [this.backgroundColor]: true
                },
                true
            );
        },
    },
    methods: {
        clearInput(e){
            if(!this.disabled){
                this.$emit('input', '');
                this.$emit('submit', e);
                this.$refs['input'].blur();
            }
        },
        retryInput(){
            this.$emit('input', this.value);
            this.submit(this.value);
        },
        input(e) {
            this.$emit('input', e.target.value);
            this.submit(e);
        },
        focus(e){
            this.$refs.input.focus();
        },
        blur(e){
            this.placeholder = 'Search';
            this.$refs.input.blur();

        },
        focusEvent(e){
            this.$emit('focus', e);
        },
        blurEvent(e){
            this.$emit('blur', e);
        },
        submit: debounce(function (e) {
            this.$emit('submit', e);
        }, 500, {leading: true}),
    }
};
</script>
<style lang="scss">
    .SearchBar {
        position: relative;
        height: 2rem;
        min-width: 11rem;
        border-radius: 4px;

        @include loading(2rem, 11rem);

        &__search {
            width: 100%;
            display: block;
            border: 1px solid $grey-100;
            border-radius: 4px;
            height: 2rem;
            font-size: 0.875rem;
            line-height: 1.875rem;
            padding: 0 1.75rem;
            transition: border-color 0.3s, color 0.3s, background-color 0.3s;
            background-color: $grey-50;

            &--grey,
            &--white {
                &:focus {
                    border: 1px solid $primary;
                    outline:none;
                    &::placeholder {
                        color: $text-grey;
                        opacity: 0.5;
                        padding-left: 0.25rem;
                    }
                }
            }
            &--grey {
                background: $white;
                border: transparent;
                box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.08);
            }
            &--fetching,
            &--saving {
                border-color: $secondary-purple-300;
                &:focus {
                    border-color: $secondary-purple-300;
                }
            }
            &--success {
                border-color: $success-green;
            }
            &--error {
                border-color: $error-red;
                &:focus {
                    border-color: $error-red;
                }
            }
        }

        &__search-icon,
        &__clear-icon {
            top: 0;
            bottom: 0;
            position:absolute;
            display: flex;
            height: fit-content;
            margin: auto 0;
        }

        &__search-icon {
            left: 0.5rem;
        }

        &__clear-icon {
            cursor: pointer;
            right: 0.5rem;
        }
    }
</style>

<docs>
```vue
import stat from 'status/index';

<RPSearchBar
    v-model="myValString"
/>

<p>For use on a grey background:</p>
<RPSearchBar
    v-model="myValString"
    backgroundColor="grey"
/>

<p>Loading state:</p>
<RPSearchBar
    v-model="myValString"
    :status="stat.LOADING"
/>
```
</docs>
